/**
 * Server-side HTTP adapter using ofetch.
 */

import { $fetch } from 'ofetch'
import type { HttpRequest, HttpResponse } from '../../../types/api'
import { CsdError } from '../../../types/error'
import { ERROR_CODES } from '../../../constants/errors'

export async function serverAdapter(request: HttpRequest): Promise<HttpResponse> {
  const { url, method = 'GET', headers, data, timeout = 30000, baseURL = '' } = request

  const fullUrl = baseURL ? `${baseURL}${url}` : url

  try {
    const response = await $fetch.raw(fullUrl, {
      method,
      headers: headers as Record<string, string>,
      body: data as BodyInit | Record<string, unknown> | null | undefined,
      timeout,
      // Keep HTTP error responses available so the SDK can normalize them.
      ignoreResponseError: true
    })

    const responseHeaders: Record<string, string> = {}
    response.headers.forEach((value: string, key: string) => {
      responseHeaders[key] = value
    })

    const httpResponse: HttpResponse = {
      data: response._data,
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      config: request
    }

    if (response.status >= 400) {
      throw new CsdError(
        `HTTP Error: ${response.status} ${response.statusText}`,
        ERROR_CODES.NETWORK_ERROR,
        httpResponse
      )
    }

    return httpResponse
  } catch (error: any) {
    if (error.name === 'TimeoutError' || error.code === 'ETIMEDOUT') {
      throw new CsdError('Request timeout', ERROR_CODES.TIMEOUT_ERROR, { config: request })
    }

    if (error.name === 'FetchError' || error.code === 'ECONNREFUSED') {
      throw new CsdError('Network error', ERROR_CODES.NETWORK_ERROR, { config: request })
    }

    throw error
  }
}
